"use client";

import { useState } from "react";
import axios from "axios";
import { signOut } from "next-auth/react";
import Button from "@/components/buttons";
import TextComponent from "@/app/_components/TextComponent";
import cn from "@/utils/cn";
import { useZStore } from "@/utils/z-store";
import Image from "next/image";
import CloseIcon from "./assets/close-icon.svg";
import { trackGAEvent } from "@/utils/ga";

type Step = "warning" | "confirm" | "processing" | "success";

const DeleteAccountModal: React.FC = () => {
  const { actions } = useZStore();
  const [step, setStep] = useState<Step>("warning");
  const [confirmationText, setConfirmationText] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    status: "immediate" | "scheduled";
    scheduledDeletionDate?: string;
  } | null>(null);

  const CONFIRMATION_PHRASE = "DELETE MY ACCOUNT";
  const isConfirmationValid = confirmationText === CONFIRMATION_PHRASE;

  const handleProceed = () => setStep("confirm");

  const handleConfirmDeletion = async () => {
    if (!isConfirmationValid) return;

    trackGAEvent("delete_account_confirm", { reason: reason || "none" });

    setStep("processing");
    setError(null);

    try {
      const response = await axios.post("/api/account/delete", {
        confirmationPhrase: confirmationText,
        reason,
      });

      trackGAEvent("delete_account_success", {
        status: response.data.status,
      });

      setResult(response.data);
      setStep("success");

      // If immediate deletion, sign out after a delay
      if (response.data.status === "immediate") {
        setTimeout(() => {
          signOut({ callbackUrl: "/" });
        }, 3000);
      }
    } catch (err) {
      setStep("confirm");
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.error || "Failed to initiate account deletion"
        );
      } else {
        setError("An unexpected error occurred");
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const modalWrapperClasses = cn(
    "relative rounded-[20px] bg-white shadow-md",
    "flex flex-col p-6 xl:p-8",
    "w-[320px] md:w-[500px] xl:w-[550px]"
  );

  // Warning Step
  if (step === "warning") {
    return (
      <div className={modalWrapperClasses}>
        <button
          className="absolute right-5 top-5 cursor-pointer hover:opacity-60"
          onClick={() => actions.closeModal()}
        >
          <Image src={CloseIcon} alt="close icon" />
        </button>

        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100">
            <span className="text-3xl">!</span>
          </div>
          <TextComponent type="h2" className="mb-2 text-red-600">
            Delete Your Account?
          </TextComponent>
        </div>

        <div className="mb-6 space-y-4">
          <TextComponent type="body" className="text-kd-secondary-2">
            This action will permanently delete:
          </TextComponent>
          <ul className="list-inside list-disc space-y-1 text-sm text-kd-secondary-3">
            <li>Your account and profile information</li>
            <li>All connected Mailchimp accounts and data</li>
            <li>All email audit reports and forms</li>
            <li>Form submissions and leads</li>
            <li>Custom dashboards and templates</li>
            <li>All associated files and assets</li>
          </ul>
          <TextComponent type="body" className="font-semibold text-red-600">
            This action cannot be undone.
          </TextComponent>
        </div>

        <div className="flex gap-4">
          <Button
            variant="secondary"
            className="h-[40px] flex-1"
            onClick={() => actions.closeModal()}
          >
            <TextComponent type="button">Cancel</TextComponent>
          </Button>
          <Button
            variant="primary"
            className="h-[40px] flex-1 !border-red-600 !bg-red-600 hover:!border-red-700 hover:!bg-red-700"
            onClick={handleProceed}
          >
            <TextComponent type="button">I Understand, Continue</TextComponent>
          </Button>
        </div>
      </div>
    );
  }

  // Confirmation Step
  if (step === "confirm") {
    return (
      <div className={modalWrapperClasses}>
        <button
          className="absolute right-5 top-5 cursor-pointer hover:opacity-60"
          onClick={() => actions.closeModal()}
        >
          <Image src={CloseIcon} alt="close icon" />
        </button>

        <TextComponent type="h2" className="mb-6 text-center text-red-600">
          Confirm Account Deletion
        </TextComponent>

        <div className="mb-6 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Type &quot;{CONFIRMATION_PHRASE}&quot; to confirm:
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              className={cn(
                "w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 text-[10.5px] xl:text-[14px] text-kd-secondary-1 placeholder:text-kd-secondary-3 font-normal",
                isConfirmationValid
                  ? "border-green-500 focus:ring-green-500"
                  : "border-gray-300 focus:ring-red-500"
              )}
              placeholder="Type the phrase above"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Why are you leaving? (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 text-[10.5px] xl:text-[14px] text-kd-secondary-1 placeholder:text-kd-secondary-3 font-normal"
              rows={3}
              placeholder="Your feedback helps us improve..."
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <Button
            variant="secondary"
            className="h-[40px] flex-1"
            onClick={() => setStep("warning")}
          >
            <TextComponent type="button">Go Back</TextComponent>
          </Button>
          <Button
            variant="primary"
            className="h-[40px] flex-1 !border-red-600 !bg-red-600 hover:!border-red-700 hover:!bg-red-700 disabled:opacity-50"
            onClick={handleConfirmDeletion}
            disabled={!isConfirmationValid}
          >
            <TextComponent type="button">Delete My Account</TextComponent>
          </Button>
        </div>
      </div>
    );
  }

  // Processing Step
  if (step === "processing") {
    return (
      <div className={modalWrapperClasses}>
        <div className="py-8 text-center">
          <div className="mx-auto mb-4 size-16 animate-spin rounded-full border-4 border-red-600 border-t-transparent" />
          <TextComponent type="h3" className="text-kd-secondary-1">
            Processing your request...
          </TextComponent>
          <TextComponent type="body" className="mt-2 text-kd-secondary-3">
            Please wait while we process your account deletion request.
          </TextComponent>
        </div>
      </div>
    );
  }

  // Success Step
  if (step === "success") {
    return (
      <div className={modalWrapperClasses}>
        <div className="py-8 text-center">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-100">
            <span className="text-3xl text-green-600">&#10003;</span>
          </div>
          <TextComponent type="h2" className="mb-4 text-green-600">
            {result?.status === "immediate"
              ? "Account Deleted"
              : "Deletion Scheduled"}
          </TextComponent>

          {result?.status === "immediate" ? (
            <TextComponent type="body" className="text-kd-secondary-3">
              Your account has been permanently deleted. You will be signed out
              shortly.
            </TextComponent>
          ) : (
            <>
              <TextComponent type="body" className="mb-2 text-kd-secondary-3">
                Your account will be deleted on{" "}
                <strong>{formatDate(result?.scheduledDeletionDate!)}</strong>
              </TextComponent>
              <TextComponent type="body" className="text-kd-secondary-3">
                You can still access your account until then. The deletion can
                be cancelled from your account settings.
              </TextComponent>
            </>
          )}
        </div>

        <Button
          variant="secondary"
          className="h-[40px] w-full"
          onClick={() => actions.closeModal()}
        >
          <TextComponent type="button">Close</TextComponent>
        </Button>
      </div>
    );
  }

  return null;
};

export default DeleteAccountModal;
